import {
    HttpContextToken,
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from '../service';
import { ToastService } from '@services/common/toast.service';
import { Injectable, Injector } from '@angular/core';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { AuthSessionTransitionService } from '../service/auth-session-transition.service';

export const BYPASS_ERROR = new HttpContextToken(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    /**
     * @param {AuthenticationService} authenticationService
     * @param {Router} router
     * @param {ToastService} toastService
     */
    constructor(
        private authenticationService: AuthenticationService,
        private router: Router,
        private injector: Injector,
        private appRouting: AppRoutingService,
        private authSessionTransition: AuthSessionTransitionService,
    ) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const toastService = this.injector.get(ToastService);

        return next.handle(request).pipe(
            catchError((err: HttpErrorResponse) => {
                if (request.context.get(BYPASS_ERROR)) {
                    throw new Error('errorMessage');
                }
                const errorBody = err.error as { translationKey?: string } | string | null;
                if (err) {
                    switch (err.status) {
                        case 400:
                            // Display generic error
                            this.router.navigate(['/pages/error']);
                            break;
                        case 401:
                            // Logout without a valid Bearer cannot blacklist server-side; avoid re-entering logout.
                            if (request.url.includes('/auth/logout')) {
                                break;
                            }
                            if (
                                typeof errorBody === 'string' &&
                                errorBody.includes("Unauthorized: The organization's license is invalid or expired.")
                            ) {
                                // Redirect to login with custom parameters
                                this.authenticationService.logout(false).subscribe(() => {
                                    this.router.navigate(['/auth/login'], { queryParams: { licenseExpired: true } });
                                });
                            } else {
                                // If we are not on the login page, relogout. This condition is usefull when the license is expired and we are on the login page.
                                if (!this.router.url.includes('/auth/login')) {
                                    this.authenticationService.logout(true, false).subscribe();
                                }
                            }
                            break;
                        case 403:
                            // During logout, org/questionnaire calls often complete as 403 after the session ends;
                            // do not navigate away from the login flow.
                            if (
                                this.authSessionTransition.isLogoutInProgress() ||
                                this.router.url.includes('/auth/login')
                            ) {
                                break;
                            }
                            this.router.navigate(['/pages/access-denied']);
                            break;
                        case 404:
                            // Display NotFoundComponent
                            this.router.navigate(['/pages/not-found']);
                            break;
                        case 409:
                            //If error contains translationKey, show error message with translation key else redirect to error page
                            if (typeof errorBody === 'object' && errorBody?.translationKey) {
                                toastService.showError({ details: `errors.backend.${errorBody.translationKey}` }, true);
                            } else {
                                this.router.navigate(['/pages/error']);
                            }

                            break;
                        case 422:
                            this.authenticationService.logout().subscribe();
                            break;
                        default:
                            //if the status error is 500 or higher keep the same structure but always redirect to login page
                            if (err.status >= 500) {
                                const translationKey =
                                    typeof errorBody === 'object' && errorBody?.translationKey
                                        ? `errors.backend.${errorBody.translationKey}`
                                        : 'errors.500';
                                toastService.showError({ details: translationKey }, true);
                            } else {
                                toastService.showError({ details: 'errors.general' }, true);
                            }
                    }
                }
                throw new Error('errorMessage');
            }),
        );
    }
}
